import * as React from 'react';
import { useContext } from 'react';
import i18n from 'i18next';
import { de, enGB, kk, ru, hy, ka, tr, cs, be, zhCN, ko, hu, srLatn, uz } from 'date-fns/locale';
import {
	format as originalFormat,
	Locale as LocaleSchema,
	parseISO,
	formatDistance as originalFormatDistance
} from 'date-fns';
import { Channel, eventChannel } from 'redux-saga';
import * as Sentry from '@sentry/react';
import { ReserveEngines } from './context';
import cn from 'classnames';

import {
	AviaPassengerType,
	TravellerFieldEnum,
	GdsServiceProductStatus,
	CheckoutOrderFragment,
	SsrStatus,
	PassengerSelectedGdsServicesFragment,
	SelectedSeatFragment,
	Position,
	FareFamilyPriceFragment,
	PlainFlightFragment,
	Gender,
	InsuranceType,
	InsuranceProgramFragment,
	FlightFragment,
	SegmentFragment,
	GdsServiceFragment,
	BaggageType,
	GdsServiceVipSubType,
	SelectedInsuranceFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	FlightSeatMapSegmentFragment
} from '@websky/graphql';
import {
	ApolloErrorPayload,
	ClassesMap,
	CommonTravellerType,
	DateFormatOptions,
	DeepPartial,
	Locale,
	Money,
	PassengerValueName,
	TravelersById,
	TravellerInfo,
	WebskyEvent,
	WithTypename
} from './types';
import { getCurrency, getLocaleFromLocalStorage, setLocaleToLocalStorage } from './cache';
import memes from './memes';
import { EngineTheme } from './theme';
import { useMediaQuery } from 'react-responsive';
import dataLayerAnalytics from './DataLayerAnalytics/DataLayerAnalytics';
import { PassengerState } from './Checkout/store/passengers/reducers';
import tg from './Locales/tg';
import { LoadingServicesParamsState, LoadingServiceType } from './Checkout/store/loadingServicesParams/reducers';

dataLayerAnalytics.init({
	currency: getCurrency()
});

export const STAY_LOGGED_IN = 'STAY_LOGGED_IN';
export const API_DATE_FORMAT = 'yyyy-MM-dd';
export const DATE_REGEXP = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const SHOW_RECAPTCHA = 'SHOW_RECAPTCHA';
export const CORPORATE_CARD = 'CORPORATE_CARD';
export const TABLET_MIN_WIDTH = 1024;
export const TABLET_MIDDLE_WIDTH = 768;
export const MOBILE_MIN_WIDTH = 600;
export const LOCALES = new Set([
	Locale.Russian,
	Locale.English,
	Locale.German,
	Locale.Kazakh,
	Locale.French,
	Locale.Chinese,
	Locale.Armenian,
	Locale.Georgian,
	Locale.Turkish,
	Locale.Tajik,
	Locale.Czech,
	Locale.Belarusian,
	Locale.Korean,
	Locale.Hungarian,
	Locale.Serbian
]);
export const SOGAZ_INSURANCE_CODES = ['gen_trans_sogaz_avia_rw'];

interface TravellerWithServices {
	services?: {
		seats?: SelectedSeatFragment[];
		gdsServices?: {
			services?: PassengerSelectedGdsServicesFragment[];
		};
	};
}

export const convertICULocale = (locale: Locale | string) => {
	/**
	 * ICU format locale for kazakh is kk, but on tickets.scat.kz but the site uses kz,
	 * in this function we convert it
	 */
	const localeLangMap = new Map<string, string>([['kk', 'kz']]);

	if (!localeLangMap.has(locale)) {
		return locale;
	} else {
		return localeLangMap.get(locale);
	}
};

interface InfoOptions {
	inline?: boolean;
	source?: boolean;
	maxPropsIntoLine?: number;
	maxPropArrayLength?: number;
	[key: string]: unknown;
}

export const getInfo = (options: InfoOptions = {}): { info: InfoOptions } => ({
	info: { inline: true, source: true, maxPropsIntoLine: 1, maxPropArrayLength: 2, ...options }
});

export const isDemo = document.location.href.includes('demo.websky.tech');

// ------------------- THEME ---------------------------------------------------------------------

export type ThemeContextType<T> = { [P in keyof T]: Partial<T[P]> };

export interface ModuleTheme<T> {
	Context: React.Context<ThemeContextType<T>>;
	Consumer: React.Consumer<ThemeContextType<T>>;
	Provider: React.Provider<ThemeContextType<T>>;
	withTheme: <C extends React.ComponentClass<any, any>>(Component: C) => C;
	useTheme: () => ThemeContextType<T>;
}

export interface User {
	values: {
		name?: string;
		value?: string;
		type?: TravellerFieldEnum;
	}[];
}

export const createWithThemeDecorator = <T extends {}>(Consumer: React.Consumer<T>) => {
	return <C extends React.ComponentClass>(Component: any): C => {
		const NewComponent = (class extends React.Component<any> {
			public render(): React.ReactNode {
				return <Consumer>{context => <Component {...this.props} theme={context} />}</Consumer>;
			}
		} as any) as C;

		NewComponent.displayName = Component.displayName;

		return NewComponent;
	};
};

export const createTheme = <T extends {}>(defaultTheme: T): ModuleTheme<T> => {
	const Context = React.createContext<ThemeContextType<T>>(defaultTheme);

	return {
		Context: Context,
		Consumer: Context.Consumer,
		Provider: Context.Provider,
		withTheme: createWithThemeDecorator<ThemeContextType<T>>(Context.Consumer),
		useTheme: () => useContext<ThemeContextType<typeof defaultTheme>>(Context)
	};
};

let currentLocale = getLocaleFromLocalStorage();

const syncCurrentLocaleWithI18n = () => {
	const updateLocaleIfNeeded = (lng: string) => {
		if (lng && lng !== currentLocale) {
			currentLocale = lng as Locale;
		}
	};

	if (i18n.isInitialized) {
		updateLocaleIfNeeded(i18n.language);
	} else {
		i18n.on('initialized', ({ lng }) => {
			updateLocaleIfNeeded(lng);
		});
	}
};

syncCurrentLocaleWithI18n();

// const defaultResources: i18n.Resource = {};
export let currentCurrency = getCurrency();

// No cached locale - pull from local storage.
if (!currentCurrency) {
	currentCurrency = getCurrency();
}

export const getLocale = () => {
	if (!i18n.isInitialized) {
		const lsLocale = getLocaleFromLocalStorage();

		return lsLocale ? lsLocale : Locale.Russian;
	}

	return i18n.language as Locale;
};

const changeLanguage = async (locale: Locale) => {
	setLocaleToLocalStorage(locale);
	currentLocale = locale;

	return i18n.changeLanguage(locale);
};

export const changeLocale = async (locale: Locale) => {
	if (LOCALES.has(locale)) {
		if (i18n.isInitialized) {
			await changeLanguage(locale);
		} else {
			i18n.on('initialized', async () => {
				await changeLanguage(locale);
			});
		}
	}
};

export const extendI18n = memes((newResources: i18n.Resource) => {
	if (!i18n.isInitialized) {
		i18n.on('initialized', () => {
			addResourcesToI18n(newResources);
		});
	} else {
		addResourcesToI18n(newResources);
	}
});

const addResourcesToI18n = (newResources: i18n.Resource) => {
	for (const locale in newResources) {
		if (newResources.hasOwnProperty(locale)) {
			for (const moduleName in newResources[locale]) {
				if (newResources[locale].hasOwnProperty(moduleName)) {
					i18n.addResourceBundle(locale, moduleName, newResources[locale][moduleName], true, true);
					console.log('Resource bundle added:', locale, moduleName);
				}
			}
		}
	}
};

let userLocation: Position;

export const getUserLocation = (onSuccess: (position: Position) => void, onError?: (error: string) => void) => {
	if (userLocation) {
		onSuccess(userLocation);
	} else if (navigator && navigator.geolocation) {
		navigator.geolocation.getCurrentPosition(
			({ coords }) => {
				if (coords.longitude && coords.latitude) {
					userLocation = {
						longitude: coords.longitude.toString(),
						latitude: coords.latitude.toString()
					};

					onSuccess(userLocation);
				}
			},
			error => onError && onError(error.message)
		);
	} else if (onError) {
		onError('GeoLocation API is not available for that kind of browsers');
	}
};

export const format = (date: Date | string | number, format: string, options?: DateFormatOptions) => {
	let resultFormattedDate = '';
	let localeObject: LocaleSchema;

	switch (currentLocale) {
		case Locale.Russian:
			localeObject = ru;
			break;

		case Locale.English:
			localeObject = enGB;
			break;

		case Locale.Tajik:
			localeObject = tg;
			break;

		case Locale.Georgian:
			localeObject = ka;
			break;

		case Locale.Armenian:
			localeObject = hy;
			break;

		case Locale.German:
			localeObject = de;
			break;

		case Locale.Kazakh:
			localeObject = kk;
			break;

		case Locale.Turkish:
			localeObject = tr;
			break;

		case Locale.Czech:
			localeObject = cs;
			break;

		case Locale.Belarusian:
			localeObject = be;
			break;

		case Locale.Chinese:
			localeObject = zhCN;
			break;

		case Locale.Korean:
			localeObject = ko;
			break;

		case Locale.Hungarian:
			localeObject = hu;
			break;

		case Locale.Serbian:
			localeObject = srLatn;
			break;

		case Locale.Uzbek:
			localeObject = uz;
			break;
	}

	try {
		const parsedDate = typeof date === 'string' ? parseISO(date) : date;

		const formattedDate = originalFormat(parsedDate, format, {
			...options,
			locale: localeObject
		});

		resultFormattedDate = formattedDate.endsWith('.')
			? formattedDate.substr(0, formattedDate.length - 1)
			: formattedDate;
	} catch (error) {
		console.error(`Date formatting error: date = ${date}\n`, error);
	}

	return resultFormattedDate;
};

export const formatDistance: typeof originalFormatDistance = (date, baseDate, options) => {
	let localeObject: LocaleSchema;

	switch (currentLocale) {
		case Locale.Russian:
			localeObject = ru;
			break;

		case Locale.English:
			localeObject = enGB;
			break;

		case Locale.Georgian:
			localeObject = ka;
			break;

		case Locale.Armenian:
			localeObject = hy;
			break;

		case Locale.German:
			localeObject = de;
			break;

		case Locale.Kazakh:
			localeObject = kk;
			break;

		case Locale.Turkish:
			localeObject = tr;
			break;

		case Locale.Czech:
			localeObject = cs;
			break;

		case Locale.Belarusian:
			localeObject = be;
			break;

		case Locale.Hungarian:
			localeObject = hu;
			break;

		case Locale.Serbian:
			localeObject = srLatn;
			break;

		case Locale.Uzbek:
			localeObject = uz;
			break;
	}

	return originalFormatDistance(date, baseDate, { ...options, locale: localeObject });
};

export const removeDotsFromDate = (dateString: string): string => {
	return dateString.replace('.', '');
};

export const debounce = <T extends (...args: any) => any>(func: T, wait: number): T => {
	let timeout: number;

	return function (...newArgs: any[]) {
		const later = function () {
			timeout = null;
			func(...newArgs);
		};

		clearTimeout(timeout);

		timeout = window.setTimeout(later, wait);
	} as any;
};

/**
 * Объединяет объекты CSS-классов, склеивая значения одинаковых ключей
 * с помощью `classnames`.
 *
 * Используется для мерджа дефолтных классов из темы и пользовательских
 * переопределений (например, из props).
 *
 *
 * Поведение:
 *
 * - Если `base` и `override` отсутствуют — возвращает пустой объект.
 *
 * - Если передан только `base` — возвращает его поверхностную копию.
 *
 * - Если передан только `override` — возвращает его поверхностную копию.
 *
 * - Если переданы оба объекта:
 *   - берётся объединение ключей из `base` и `override`;
 *   - для каждого ключа значения объединяются через `classnames`;
 *   - значение из `base` всегда идёт первым,
 *     значение из `override` — вторым.
 *
 *
 * Гарантии функции:
 *
 * - входные объекты не мутируются;
 *
 * - всегда возвращается новый объект;
 *
 * - корректно обрабатываются `null`, `undefined`,
 *   пустые строки и отсутствующие ключи.
 *
 *
 * @typeParam A
 * Тип объекта базовых классов.
 *
 * @typeParam B
 * Тип объекта переопределяемых классов.
 *
 *
 * @param base
 * Базовый объект классов (например, из темы).
 *
 * @param override
 * Объект классов для переопределения (например, из props).
 *
 *
 * @returns
 * Новый объект классов, содержащий объединение ключей `base` и `override`.
 *
 *
 * @example
 * ```ts
 * mergeClasses(
 *   { button: 'btn', icon: 'icon' },
 *   { button: 'btn_primary' }
 * );
 * // => { button: 'btn btn_primary', icon: 'icon' }
 * ```
 */
export const mergeClasses = <A extends ClassesMap, B extends ClassesMap>(
	base?: Partial<A> | null,
	override?: Partial<B> | null
): A & B => {
	// если ничего нет — возвращаем пустой объект
	if (!base && !override) {
		return {} as A & B;
	}

	// если есть только base — возвращаем его копию
	if (base && !override) {
		return { ...base } as A & B;
	}

	// если есть только override — возвращаем его копию
	if (!base && override) {
		return { ...override } as A & B;
	}

	// иначе мерджим по объединению ключей (base и override)
	const keys = new Set<string>([...Object.keys(base), ...Object.keys(override)]);
	const result: Record<string, string | undefined> = {};

	keys.forEach(key => {
		result[key] = cn(base[key], override[key]);
	});

	return result as A & B;
};

export const mergeCSS = <T extends EngineTheme>(originalTheme: T, newTheme: T | DeepPartial<T>): T => {
	const result: T = JSON.parse(JSON.stringify(originalTheme));

	const mergeRecursive = (target: object, source: object): void => {
		for (const key in source) {
			if (!source.hasOwnProperty(key) || !target.hasOwnProperty(key)) {
				continue;
			}

			const sourceValue = source[key];
			const targetValue = target[key];

			if (typeof sourceValue === 'string' && typeof targetValue === 'string') {
				target[key] = `${targetValue} ${sourceValue}`;
			} else if (
				typeof sourceValue === 'object' &&
				typeof targetValue === 'object' &&
				sourceValue !== null &&
				targetValue !== null &&
				!Array.isArray(sourceValue) &&
				!Array.isArray(targetValue)
			) {
				mergeRecursive(targetValue, sourceValue);
			}
		}
	};

	mergeRecursive(result, newTheme);

	return result;
};

export const decapitalizeFirstLetter = memes((string: string): string => {
	return string.charAt(0).toLowerCase() + string.substr(1);
});

const capitalizeFirstLetterSplittedBy = (string: string, separator: string) => {
	if (string.includes(separator)) {
		return string
			.split(separator)
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(separator);
	}

	return string;
};

export const capitalizeFirstLetter = memes((string: string): string => {
	if (!string) {
		return string;
	}

	const lowerString = string.toLowerCase();

	let newString = lowerString.charAt(0).toUpperCase() + lowerString.slice(1);

	newString = capitalizeFirstLetterSplittedBy(newString, ' ');

	return newString;
});

export const capitalizeFirstLetterInName = memes((string: string): string => {
	const newString = capitalizeFirstLetter(string);

	return capitalizeFirstLetterSplittedBy(newString, '-');
});

export const base64ToBlob = (base64: string, mimeType: string) => {
	const binaryString = window.atob(base64);

	const length = binaryString.length;
	const bytes = new Uint8Array(length);

	for (let i = 0; i < length; i++) {
		bytes[i] = binaryString.charCodeAt(i);
	}

	return new Blob([bytes], { type: mimeType });
};

export const downloadFile = (base64: string, name: string, mimeType: string): void => {
	const blob = base64ToBlob(base64, mimeType);
	const url = URL.createObjectURL(blob);

	const element = document.createElement('a');
	element.href = url;
	element.download = name;
	element.style.display = 'none';

	document.body.appendChild(element);
	element.click();
	document.body.removeChild(element);

	URL.revokeObjectURL(url);
};

export const downloadPDF = (base64: string, name: string): void => {
	downloadFile(base64, name, 'application/pdf');
};

export const downloadPkpass = (base64: string, name: string): void => {
	downloadFile(base64, name, 'application/vnd.apple.pkpass');
};

export const enableSentry = () => {
	if (process.env.NODE_ENV === 'production' && process.env.SENTRY_SELF_HOSTED_DSN) {
		Sentry.init({
			dsn: process.env.SENTRY_SELF_HOSTED_DSN,
			environment: process.env.PLATFORM,
			// Трейсинг отключён: высокая нагрузка на клиент
			tracesSampleRate: 0,
			beforeSend(event) {
				// Check if the URL fragment exists
				const locationHash = window.location.hash;

				if (locationHash) {
					// Add the URL fragment as a tag
					event.tags = {
						...event.tags, // Keep existing tags
						locationHash: locationHash
					};
				}

				return event;
			},
			allowUrls: [
				'https://static.flyredwings.com/',
				'https://cdn.websky.aero/',
				'https://cdn-eu1.websky.aero/',
				'https://cdn.eu2.websky.tech/',
				'https://cdn.web.websky.tech/',
				'https://static.belavia.by',
				'https://cdn.webskyx.com'
			]
		});
	}
};

export const captureEventSentry = (event: CustomEvent<ApolloErrorPayload>) => {
	if (process.env.NODE_ENV === 'production' && process.env.SENTRY_SELF_HOSTED_DSN) {
		Sentry.captureMessage(event.detail.message, 'error');
	}
};

export const captureErrorSentry = (message: string, level: Sentry.SeverityLevel = 'error') => {
	if (process.env.NODE_ENV === 'production' && process.env.SENTRY_SELF_HOSTED_DSN) {
		Sentry.captureMessage(message, level);
	}
};

export const removeTrailingSlash = (string: string): string => {
	const stringLength = string.length;

	return string.charAt(stringLength - 1) === '/' ? string.substr(0, stringLength - 1) : string;
};

export const setsAreEqual = (a: Set<any>, b: Set<any>): boolean => {
	if (a.size !== b.size) {
		return false;
	}

	for (const index of a) {
		if (!b.has(index)) {
			return false;
		}
	}

	return true;
};

export const isOdd = (int: number | string): boolean => (typeof int === 'string' ? parseInt(int, 10) : int) % 2 === 1;

export const useIsMobile = (): boolean => {
	return useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
};

export const useIsTabletMiddle = (): boolean => {
	return useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });
};

export const useIsTablet = (): boolean => {
	return useMediaQuery({ maxWidth: TABLET_MIN_WIDTH });
};

export const useIsDesktop = (): boolean => {
	return useMediaQuery({ minWidth: TABLET_MIN_WIDTH });
};

export const scrollTo = (top: number) => {
	if (window.hasOwnProperty('scrollTo')) {
		window.scrollTo({
			top,
			behavior: 'smooth'
		});
	}
};

export const getUserValue = (user: User, type: TravellerFieldEnum) => {
	return user.values?.find(value => value.type === type)?.value ?? '';
};

export const getPassengerNames = (passenger: User) => {
	const firstName = getUserValue(passenger, TravellerFieldEnum.FirstName);
	const lastName = getUserValue(passenger, TravellerFieldEnum.LastName);
	const middleName = getUserValue(passenger, TravellerFieldEnum.MiddleName);

	return { firstName, lastName, middleName };
};

export const getUserArray = (passengerValue: PassengerValueName) => {
	const { firstName, lastName, middleName } = passengerValue;

	return [
		{ name: 'firstName', value: firstName || '' },
		{ name: 'lastName', value: lastName || '' },
		{ name: 'middleName', value: middleName || '' }
	];
};

/**
 * Формирует массив строк по шаблону `pattern`, сопоставляя его с данными пользователя.
 * Если токен в шаблоне равен `lastName`, `firstName` или `middleName`, результат будет приведён к виду с заглавной буквы.
 *
 * @param values Массив значений пользователя с полями `name` и `value`.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Массив значений, соответствующих шаблону.
 */
export const getPassengerLabelAsArray = (values: User['values'], pattern?: string): string[] => {
	// Безопасное получение паттерна с fallback
	let finalPattern = pattern;
	if (!finalPattern) {
		if (i18n.isInitialized && i18n.exists('fullPassengerNamePattern')) {
			finalPattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian });
		} else {
			finalPattern = 'lastName firstName middleName';
		}
	}

	const tokens = finalPattern.split(' ');
	const resultValues: string[] = [];

	tokens.forEach(token => {
		const value = values?.find(value => value?.name === token)?.value ?? '';

		if (value && (token === 'lastName' || token === 'firstName' || token === 'middleName')) {
			resultValues.push(capitalizeFirstLetterInName(value.trim()));
		} else if (value) {
			resultValues.push(value);
		}
	});

	return resultValues;
};

/**
 * Формирует строку имени пассажира по заданному шаблону `pattern`.
 * Использует данные `firstName`, `lastName` и `middleName`.
 *
 * @param passengerValue Объект с данными пассажира.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Строка, собранная из переданных данных в порядке, заданном шаблоном.
 */
export const concatPassengerNames = (
	passengerValue: PassengerValueName,
	pattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian })
) => {
	const userArray = getUserArray(passengerValue);

	return getPassengerLabelAsArray(userArray, pattern).join(' ');
};

/**
 * Возвращает строку с инициалами пассажира (например: "ИП" для Иван Петров).
 *
 * @param passengerValue Объект с данными пассажира.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Строка с первыми буквами каждого значения в шаблоне.
 */
export const getFirstCharName = (
	passengerValue: PassengerValueName,
	pattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian })
) => {
	const userArray = getUserArray(passengerValue);

	return getPassengerLabelAsArray(userArray, pattern)
		.map(value => (value || '').trim()[0])
		.join('');
};

/**
 * Возвращает аватар пассажира в виде инициалов (например, "ИП") или числового ID + 1, если инициалы не найдены.
 *
 * @param passengerState Состояние пассажира, содержащее `id`, `firstName`, `lastName` и т.д.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Строка-аватар: инициалы или число.
 */
export const getPassengerAvatar = (
	passengerState: PassengerState,
	pattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian })
) => {
	const { firstName, lastName } = getPassengerNames(passengerState);
	const passengerInitials = getFirstCharName({ lastName, firstName }, pattern);

	return passengerInitials ? passengerInitials : (parseInt(passengerState.id) + 1).toString();
};

/**
 * Возвращает строку с фамилией и именем пассажира по заданному шаблону.
 * Используются только поля `lastName` и `firstName`.
 *
 * @param passenger Объект пользователя с массивом значений.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale passengerNamePattern = 'lastName firstName'.
 * @returns Строка в формате "Фамилия Имя".
 */
export const getTravellerName = (
	passenger: User,
	pattern = i18n.t('passengerNamePattern', { fallbackLng: Locale.Russian })
) => {
	return getPassengerLabelAsArray(passenger.values || [], pattern).join(' ');
};

/**
 * Возвращает полное ФИО пассажира (фамилия, имя, отчество) по шаблону.
 *
 * @param passenger Объект пользователя с массивом значений.
 * @param pattern Строка-шаблон, определяющая порядок имени. По дефолту задается в ru-locale fullPassengerNamePattern = 'lastName firstName middleName'.
 * @returns Строка в формате "Фамилия Имя Отчество".
 */
export const getTravellerFullName = (
	passenger: User,
	pattern = i18n.t('fullPassengerNamePattern', { fallbackLng: Locale.Russian })
) => {
	return getPassengerLabelAsArray(passenger?.values || [], pattern).join(' ');
};

export const getPassengerTypeLabel = (type: AviaPassengerType, supplierType?: string, count?: number) => {
	const options: i18n.TOptions = { count };

	if (supplierType && i18n.exists(`PassengerTypes:${supplierType}`)) {
		return i18n.t(`PassengerTypes:${supplierType}`, options);
	}

	return i18n.t(`PassengerTypes:${type}`, options);
};

export const getPassengerLabel = (passenger: PassengerState) => {
	const { firstName, lastName, middleName } = getPassengerNames(passenger);

	if (firstName && lastName) {
		return concatPassengerNames({ lastName, firstName, middleName });
	}

	return getPassengerTypeLabel(passenger.type, passenger.supplierTravellerType);
};

export const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const seatNumberRegex = /[0-9]{1,4}[A-Z]/;

/**
 * Create a bubbling CustomEvent and dispatch on target.
 *
 * @param target HTMLElement
 * @param event
 */
export const emitEvent = (target: HTMLElement | Document, event: WebskyEvent) => {
	try {
		target.dispatchEvent(
			new CustomEvent(event.type, {
				detail: event.data,
				bubbles: true
			})
		);
	} catch (e) {
		console.warn('Cannot to emit event ' + event.type);
	}
};

export const isRequireCurrencyParam = () => {
	return ['A9', 'RM', '5G', '2S', 'JI', 'DV', 'B2', 'WZ', 'SZ', 'HH'].includes(
		window.__webskyEngineConfig?.global?.companyInfo?.iataCode
	);
};

export const getTravellersMap = memes(
	(travellers: CommonTravellerType[]): TravelersById => {
		const map = new Map<string, TravellerInfo>(),
			travellerTypesCount = new Map<string, number>();

		travellers.forEach(traveller => {
			const firstName = traveller?.values?.find(value => value.type === TravellerFieldEnum.FirstName)?.value,
				lastName = traveller?.values?.find(value => value.type === TravellerFieldEnum.LastName)?.value;

			const fullName = lastName && firstName ? concatPassengerNames({ lastName, firstName }) : null;
			const typeNumber = (travellerTypesCount.get(traveller.type) || 0) + 1;

			map.set(traveller.id, {
				id: traveller.id,
				name: fullName,
				type: traveller.type,
				supplierTravellerType: traveller.supplierTravellerType,
				typeNumber: typeNumber
			});

			travellerTypesCount.set(traveller.type, typeNumber);
		});

		return map;
	}
);

export const getCheckInUrl = (
	checkInURL: string,
	orderId: string,
	ticket?: string,
	lastName?: string,
	reserveEngines?: ReserveEngines
) => {
	const formattedCheckInURL = removeTrailingSlash(checkInURL);

	if (reserveEngines?.redirectToWebsky1 && reserveEngines?.checkin) {
		const checkinURL = removeTrailingSlash(reserveEngines.checkin);
		const locale = convertICULocale(getLocale());

		const url = new URL(checkinURL);
		url.searchParams.set('lang', locale);
		url.searchParams.set('ticketNumber', ticket);
		url.searchParams.set('lastName', lastName);
		url.searchParams.set('submit', '');
		url.hash = 'search';

		return url.toString();
	} else if (lastName && ticket) {
		return `${formattedCheckInURL}#/search/${ticket}/${lastName}`;
	}

	return `${formattedCheckInURL}#/${orderId}`;
};

const variationMap = new Map<number[], number>([
	[[1], 0],
	[[2, 3, 4], 1],
	[[0, 5, 6, 7, 8, 9], 2]
]);

export const getI18nCountVariation = (count: number, variation = variationMap) => {
	const locale = getLocale();

	if (locale === 'ru') {
		const actualCount = count >= 10 && count <= 20 ? 0 : count % 10;

		return [...variation.entries()].find(([numbers]) => numbers.includes(actualCount))?.[1] ?? 0;
	}

	return count === 0 ? 0 : 2;
};

export const isUnconfirmedService = (status: GdsServiceProductStatus) => {
	return [
		GdsServiceProductStatus.Rejected,
		GdsServiceProductStatus.Canceled,
		GdsServiceProductStatus.Problematic
	].includes(status);
};

export const isRequestedService = (status: GdsServiceProductStatus) => {
	return status === GdsServiceProductStatus.Requested;
};

export function getFlatArraySet(values: string[][]): Set<string> {
	return new Set(values.reduce((result, array) => [...result, ...array], []));
}

export function getSegmentGroupsMap(segmentGroups: FlightFragment['segmentGroups']): Map<string, SegmentFragment[]> {
	const segmentsMap = new Map<string, SegmentFragment[]>();

	segmentGroups.forEach(segment => {
		segment.segments.forEach(({ id }) => {
			segmentsMap.set(id, segment.segments);
		});
	});

	return segmentsMap;
}

export const getHasRequestedServices = (
	travellers: TravellerWithServices[],
	ssrData?: CheckoutOrderFragment['ssrData']
): boolean => {
	return travellers.some(traveller => {
		const hasRequestedServices = traveller.services?.gdsServices?.services?.some(service =>
			service.products?.some(product => product?.status === GdsServiceProductStatus.Requested)
		);

		const hasRequestedSeats = traveller.services?.seats?.some(
			service => service.product?.status === GdsServiceProductStatus.Requested || service.onRequest
		);

		const hasRequestedSsrData = ssrData?.some(ssrData => ssrData.status === SsrStatus.OnRequest);

		return hasRequestedServices || hasRequestedSeats || hasRequestedSsrData;
	});
};

// TODO: remove this when php is fixed
export const convertGenderToCorrectValue = (gender: string): Gender => {
	const genderMap = {
		M: Gender.Male,
		F: Gender.Female,
		O: Gender.Other
	};

	return genderMap[gender] ?? gender;
};

export const isFlightFareM2 = (flight?: { __typename: string }): boolean => {
	return flight?.__typename === 'FlightFareM2';
};

export const isIncludePricesGroupM2Flight = (priceGroups?: FareFamilyPriceFragment[]): boolean => {
	if (!priceGroups || !priceGroups?.length) {
		return false;
	}

	return priceGroups.some(group => {
		const flight = group.prices?.[0]?.flight?.fares?.[0] as WithTypename<PlainFlightFragment['fares'][0]>;

		return isFlightFareM2(flight);
	});
};

export const isExistI18nTranslate = (key: string, options?: i18n.TOptions) => {
	if (
		i18n.options?.postProcess &&
		Array.isArray(i18n.options.postProcess) &&
		i18n.options.postProcess.includes('showKeys')
	) {
		return true;
	}

	return !!i18n.t(key, options) && i18n.exists(key, options);
};

export const getOffsetTop = (elem: HTMLElement): number => {
	let offsetTop = 0;
	let currentElement = elem;

	while (currentElement) {
		if (!isNaN(currentElement.offsetTop)) {
			offsetTop += currentElement.offsetTop;
		}

		currentElement = currentElement.offsetParent as HTMLElement;
	}

	return offsetTop;
};

/** Читает CSS-переменную с documentElement как число пикселей (например `--wx-custom-header-height`). */
export const getCssVariablePx = (name: string, fallback = 0): number => {
	if (typeof document === 'undefined') {
		return fallback;
	}

	const parsed = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name).trim());

	return Number.isFinite(parsed) ? parsed : fallback;
};

export const getRedirectToOrderUrl = (engineURL: string, orderId: string): string => {
	return `${engineURL}#/booking/${orderId}`;
};

export const redirectToOrder = (engineURL: string, orderId: string, modifyUrlFn?: (url: string) => string): void => {
	const redirectToOrderURL = getRedirectToOrderUrl(engineURL, orderId);

	window.open(modifyUrlFn?.(redirectToOrderURL) || redirectToOrderURL, '_blank');
};

export const getSelectedInsurancesByTypes = (
	selectedInsurances: SelectedInsuranceFragment[] = [],
	insuranceTypes: InsuranceType[]
): InsuranceProgramFragment[] => {
	if (!selectedInsurances.length) {
		return [];
	}

	const selectedInsuranceByCode = new Map<string, InsuranceProgramFragment>();

	selectedInsurances.forEach(insurance => {
		if (insuranceTypes.includes(insurance.insuranceProgram.type)) {
			selectedInsuranceByCode.set(insurance.insuranceProgram.code, insurance.insuranceProgram);
		}
	});

	return [...selectedInsuranceByCode.values()];
};

export const getAllSegmentsServicePrice = (service: GdsServiceFragment): Money => ({
	...service.price,
	amount: service.price.amount * service.allowedSegments.length
});

export const getAppleWalletIconUrl = () => {
	const currentLocale = getLocale();

	return {
		src: `https://cdn.websky.aero/content/frontend/images/apple-wallet/${currentLocale}/badge.svg`,
		fallbackSrc: `https://cdn.websky.aero/content/frontend/images/apple-wallet/en/badge.svg`
	};
};

export const getCroppedError = (error: string, subString = 'GraphQL error: ') =>
	error ? error.replace(subString, '') : '';

export function createExternalSagaActionChannel<T>(eventName: string): Channel<T> {
	return eventChannel(emit => {
		const listener = (event: Event) => {
			emit((event as CustomEvent).detail);
		};

		window.addEventListener(eventName, listener);

		return () => window.removeEventListener(eventName, listener);
	});
}

export function getLinkedTravellersMap(travellers: { id?: string; linkedTraveller?: { id?: string } }[]) {
	const linkedTravellersMap = new Map<string, string[]>();

	for (const traveller of travellers) {
		const travellerId = traveller.id;
		const linkedTravellerId = traveller.linkedTraveller?.id;

		if (!travellerId || !linkedTravellerId) {
			continue;
		}

		linkedTravellersMap.set(travellerId, [...(linkedTravellersMap.get(travellerId) ?? []), linkedTravellerId]);
		linkedTravellersMap.set(linkedTravellerId, [
			...(linkedTravellersMap.get(linkedTravellerId) ?? []),
			travellerId
		]);
	}

	return linkedTravellersMap;
}

export const getStringsHasChanges = (a: string[], b: string[]) => {
	if (a.length !== b.length) {
		return true;
	}

	return JSON.stringify(a) !== JSON.stringify(b);
};

export function getIsLoadingServiceByParams(
	loadingServicesTypesState: LoadingServicesParamsState,
	params: {
		id?: string;
		rfisc?: string;
		subType?: BaggageType | GdsServiceVipSubType;
		type?: OrderAdditionalServiceGdsServiceServiceType | LoadingServiceType;
	},
	loadingParamKeys: (keyof LoadingServicesParamsState)[] = ['subType', 'type']
): boolean {
	if (!params) {
		return false;
	}

	return loadingParamKeys.some(paramKey => {
		const paramValue = params[paramKey];

		if (!paramValue) {
			return false;
		}

		const loadingServicesValuesByParam: string[] = loadingServicesTypesState[paramKey];

		if (!loadingServicesValuesByParam?.length) {
			return false;
		}

		return loadingServicesValuesByParam.includes(paramValue);
	});
}

export const isImageValid = (src: string) => {
	return new Promise(resolve => {
		const img = document.createElement('img');
		img.onerror = () => resolve(false);
		img.onload = () => resolve(true);
		img.src = src;
	});
};

export const isAbsoluteUrl = (url?: string): boolean => {
	if (!url) {
		return false;
	}

	try {
		new URL(url);

		return true;
	} catch {
		return false;
	}
};

export const hasIntersection = (arr1: string[], arr2: string[]): boolean => {
	const set1 = new Set(arr1);
	for (const item of arr2) {
		if (set1.has(item)) {
			return true;
		}
	}

	return false;
};

/** Функция снимаем фокус с активного элемента (если есть) */
export const blurActiveElement = () => {
	if (typeof document !== 'undefined') {
		(document.activeElement as HTMLElement | null)?.blur?.();
	}
};

export const disableAutocompleteProps = {
	autoComplete: 'one-time-code',
	'data-bwignore': 'true',
	'data-1p-ignore': 'true',
	'data-lpignore': 'true'
};

export const getSeatMapUniqueAvailableSegments = (segments: FlightSeatMapSegmentFragment[]) => {
	const uniqueAvailableSegments = new Set<string>();

	segments.forEach(segment => {
		if (segment.availableSeatsCount) {
			uniqueAvailableSegments.add(segment.segmentsInfo.id);
		}
	});

	return [...uniqueAvailableSegments];
};
